import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { get } from "lodash";
import { makeStyles } from "@material-ui/core";
import RemoveIcon from "@material-ui/icons/Remove";
import IconButton from "@material-ui/core/IconButton";
import EditIcon from "@material-ui/icons/Edit";
import { baseUrl } from "../core/Api";
import { updateField, removeField, updateOverallWidth } from "../reducers/BertWarehouse/Actions";
import FieldOptionsDialog from "./FieldOptionsDialog";
import arrowUp from "../assets/arrowUp.svg";
import arrowUpSmall from "../assets/arrowUpSmall.svg";
import arrowDownSmall from "../assets/arrowDownSmall.svg";
import arrowDown from "../assets/arrowDown.svg";
import { useTranslation } from "react-i18next";
import { numberIsOdd } from "../Utils/Helpers";

const useStyles = makeStyles((theme) => ({
  wrapper: {
    position: "relative",
  },
  flexCol: {
    display: "flex",
    alignItems: "center",
    position: "relative",
  },
  container: {
    position: "relative",
  },
  leftSidePanel: {
    marginTop: 20,
    zIndex: 2,
  },
  rightSidePanel: {
    marginTop: 20,
    zIndex: 2,
  },
  supportBar: {
    position: "absolute",
    zIndex: 2,
  },
  iconButton: {
    backgroundColor: "#c5da86",
    borderWidth: 1,
    left: "calc(50% - 19px)",
    borderColor: "#c5da86",
    boxShadow: "0px 2px 4px #00000080",
    borderRadius: 2,
    bottom: "-10px",
    position: "absolute",
    zIndex: 15,
    padding: 4,
    "&:hover": {
      backgroundColor: "#c5da86",
    },
  },
  shelfWidth: {
    bottom: "-80px",
    position: "absolute",
    fontSize: 14,
    fontFamily: theme.typography.fontFamily,
    fontWeight: "normal",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row",
    display: "flex",
  },
  arrow: {
    display: "flex",
    alignItems: "flex-start",
    flex: 1,
  },
  shelfHeight: {
    left: 0,
    bottom: -54,
    minHeight: 54,
    position: "absolute",
    fontSize: 14,
    fontFamily: theme.typography.fontFamily,
    fontWeight: "normal",
  },
  deleteButton: {
    backgroundColor: "#788184",
    borderWidth: 1,
    borderColor: "#788184",
    boxShadow: "0px 2px 4px #00000080",
    borderRadius: 2,
    padding: 2,
    top: -60,
    left: "50%",
    position: "absolute",
    "&:hover": {
      backgroundColor: "#788184",
    },
  },
}));

const FrontViewIsoWarehouse = (data, { ...props }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation("common");
  const optionsLoaded = useSelector((state) => {
    return state.Configuration.optionsLoaded;
  });
  const shelfLoadOptions = useSelector(
    (state) =>
      state.Configuration.bertWarehouseComponentOptions.find((opt) => opt.key === "shelfLoad").options
  );
  const id = get(data, "data.id", 1);
  const height = Number(get(data, "height", 2000).match(/\d+/));
  const depth = Number(get(data, "depth", 300).match(/\d+/));
  const widthIdentifier = get(data, "data.width", 1900);
  const width = Number(get(data, "data.width", 1900).match(/\d+/));
  const supportbarIdentifier = get(data, "data.layers", 3);
  const supportBars = Number(get(data, "data.layers", 3).match(/\d+/));
  const shelfLoadIdentifier = get(data, "shelfLoad", 150);
  const shelfLoad = Number(get(data, "shelfLoad", 150).match(/\d+/));
  const color = get(data, "color", 2000);
  const twoSidedShelf = get(data, "usableFromBothSides", false);
  const doubleShelf = get(data, "doubleShelf", false);
  const leftSidePanel = get(data, "data.leftPanel", false);
  const rightSidePanel = get(data, "data.rightPanel", false);
  const scaleFactor = get(data, "scaleFactor", 8);
  const defaultShelfBottomHeight = 22;
  const shelfBottomHeight = get(data, "shelfBottomHeight", 22);
  const shelfBottomHeightDifference = shelfBottomHeight - defaultShelfBottomHeight;
  const scaledHeight = height / scaleFactor;
  const scaledWidth = (width + shelfBottomHeightDifference) / scaleFactor;
  const [showFieldOptions, setShowFieldOptions] = useState(false);
  const widthContainerMargin = width <= 1005 ? 6 : 12;
  const widthContainerMarginSmallScreen = 10;
  const widthContainerWidthSmallScreen = 40;
  const widthContainerWidth = width < 1005 ? 48 : 54;
  const idIsOdd = numberIsOdd(id);
  const isSmallScreen = scaleFactor > 8;
  let shelfWidthOffset = 160 / scaleFactor;
  if (depth === 400) {
    shelfWidthOffset = 200 / scaleFactor;
  } else if (depth === 500) {
    shelfWidthOffset = 230 / scaleFactor;
  } else if (depth === 600) {
    shelfWidthOffset = 260 / scaleFactor;
  } else if (depth === 800) {
    shelfWidthOffset =
      height > 2325 ? 330 / scaleFactor : height > 2000 ? 320 / scaleFactor : 310 / scaleFactor;
  }
  const floorHeight = shelfLoad === 80 ? 20 : 40;
  const shelfHeight = Math.floor((height - 75 - supportBars * floorHeight) / (supportBars - 1) / 25) * 25;

  const updateWidth = async (width) => {
    await dispatch(updateField(id, "width", width));
    return dispatch(updateOverallWidth());
  };
  const updateLeftSidePanel = (panel) => {
    dispatch(updateField(id, "leftPanel", panel));
  };
  const updateRightSidePanel = (panel) => {
    dispatch(updateField(id, "rightPanel", panel));
  };
  const updateSupportBarsAmount = (amount) => {
    dispatch(updateField(id, "layers", amount));
  };
  const removeCurrentField = () => {
    if (id !== 1 && id !== 2) {
      dispatch(removeField(id));
    }
  };

  const classes = useStyles({ scaleFactor });
  const selectedShelfLoadOption =
    shelfLoadOptions.length > 0
      ? shelfLoadOptions.find((option) => option.identifier === shelfLoadIdentifier)
      : {};
  const shelfLoadAttributes = selectedShelfLoadOption.attributes
    ? selectedShelfLoadOption.attributes.find((t) => t.identifier === "shelfbottoms").values
    : [];
  const isThickFrameRequired = shelfLoadAttributes.find((attributeValue) => attributeValue.value === "thick");
  const shlefWidth = isThickFrameRequired ? "thick" : "thin";
  const shelfWrapperMargin = -shelfBottomHeight * (id - 1) - shelfBottomHeightDifference * (id - 1);
  const rowElements = [];
  const calcHeight = scaledHeight - shelfBottomHeight - 48 / scaleFactor;
  const spacing = calcHeight / (supportBars - 1);
  for (let i = 0; i < supportBars; i++) {
    rowElements.push(
      <div
        className={classes.supportBar}
        key={`supportBar_${i}`}
        style={{
          minHeight: shelfBottomHeight,
          maxHeight: shelfBottomHeight,
          top: i === 0 ? 0 : i * spacing,
          zIndex: supportBars - i,
        }}
      >
        <img
          onLoad={(e) => {
            if (i === 0 && id === 1) {
              data.saveShelfHeightPerDepth(e.currentTarget.clientHeight);
            }
          }}
          src={`${baseUrl}/images/item/bert/warehouse/image/shelfbottom_steel_width_${width}_depth_${depth}_${color}_${shlefWidth}.svg`}
          style={{ maxWidth: scaledWidth, minWidth: scaledWidth, maxHeight: "100%" }}
        />
        {!twoSidedShelf && (
          <img
            src={`${baseUrl}/images/item/bert/warehouse/image/einseitig_width_${width}.svg`}
            style={{
              maxWidth: scaledWidth - shelfBottomHeight,
              maxHeight: "100%",
              position: "absolute",
              bottom: depth >= 600 || (i === 0 && scaleFactor >= 8) ? 0 : 1,
              left:
                scaleFactor < 5 ? 72 / scaleFactor : scaleFactor < 4 ? 96 / scaleFactor : 48 / scaleFactor,
            }}
          />
        )}
      </div>
    );
  }
  return (
    <div
      className={classes.container}
      style={{
        width: scaledWidth + shelfBottomHeightDifference,
        display: "flex",
        flexDirection: "inherit",
        height: "100%",
      }}
    >
      {doubleShelf && (
        <div
          className={classes.flexCol}
          style={{
            width: scaledWidth,
            height: scaledHeight,
            marginLeft:
              id === 1
                ? 0
                : depth >= 600 && width === 1285
                ? shelfWrapperMargin - 1 * (id - 1)
                : shelfWrapperMargin,
            left: shelfBottomHeight - 16 / scaleFactor,
            top: -shelfBottomHeight + 32 / scaleFactor,
            zIndex: 4,
            position: "absolute",
          }}
        >
          {id === 1 && (
            <div
              className={classes.leftPanel}
              style={{
                display: "flex",
                position: "absolute",
                top: 0,
                height: scaledHeight - shelfBottomHeight,
              }}
            >
              {!leftSidePanel ? (
                <img
                  src={`${baseUrl}/images/item/bert/warehouse/image/side_singlepanels_height_${height}_depth_${depth}_${color}.svg`}
                  className={classes.leftSidePanel}
                  style={{
                    maxHeight: scaledHeight - shelfBottomHeight + 24 / scaleFactor,
                    position: "absolute",
                    left: -16 / scaleFactor,
                    marginTop: shelfBottomHeight,
                  }}
                />
              ) : (
                <div
                  className={classes.leftSidePanel}
                  style={{
                    backgroundImage: `url(${baseUrl}/images/item/bert/warehouse/image/side_fullpanel_height_${height}_depth_${depth}.svg)`,
                    minHeight: depth === 800 ? scaledHeight + 24 / scaleFactor : scaledHeight,
                    minWidth: shelfBottomHeight,
                    marginTop: depth === 800 ? -24 / scaleFactor : 0,
                    backgroundSize: "cover",
                    position: "absolute",
                    left: depth === 800 ? -24 / scaleFactor : -16 / scaleFactor,
                  }}
                ></div>
              )}
              <img
                src={`${baseUrl}/images/item/bert/warehouse/image/frame_height_${height}_${color}_back.svg`}
                style={{
                  maxHeight: scaledHeight - shelfBottomHeight + 24 / scaleFactor,
                  position: "absolute",
                  left: shelfBottomHeight - 32 / scaleFactor,
                  top: 16 / scaleFactor,
                  display: "block",
                }}
              />
            </div>
          )}
          <div
            className={classes.wrapper}
            style={{
              minHeight: scaledHeight - 48 / scaleFactor,
              maxHeight: scaledHeight,
              minWidth: scaledWidth,
              width: scaledWidth,
            }}
          >
            {rowElements}
          </div>

          <div
            className={classes.rightPanel}
            style={{
              display: "flex",
              position: "absolute",
              top: 0,
              height: scaledHeight - shelfBottomHeight,
            }}
          >
            {!rightSidePanel ? (
              <img
                src={`${baseUrl}/images/item/bert/warehouse/image/side_singlepanels_height_${height}_depth_${depth}_${color}.svg`}
                className={classes.rightSidePanel}
                style={{
                  maxHeight: scaledHeight - shelfBottomHeight + 24 / scaleFactor,
                  position: "absolute",
                  marginTop: shelfBottomHeight,
                  right:
                    depth === 800 && height === 2500
                      ? -scaledWidth - 24 / scaleFactor
                      : depth === 800 && height === 3000
                      ? -scaledWidth - 32 / scaleFactor
                      : -scaledWidth - 16 / scaleFactor,
                  zIndex: 13,
                }}
              />
            ) : (
              <div
                className={classes.rightSidePanel}
                style={{
                  backgroundImage: `url(${baseUrl}/images/item/bert/warehouse/image/side_fullpanel_height_${height}_depth_${depth}.svg)`,
                  minHeight: depth === 800 ? scaledHeight + 24 / scaleFactor : scaledHeight,
                  minWidth: shelfBottomHeight,
                  marginTop: depth === 800 ? -24 / scaleFactor : 0,
                  backgroundSize: "cover",
                  position: "absolute",
                  right:
                    depth === 800 && height === 2500
                      ? -scaledWidth - 56 / scaleFactor
                      : depth === 800 && height === 3000
                      ? -scaledWidth - 64 / scaleFactor
                      : -scaledWidth - 24 / scaleFactor,
                  zIndex: 13,
                }}
              />
            )}
            <img
              src={`${baseUrl}/images/item/bert/warehouse/image/frame_height_${height}_${color}_back.svg`}
              style={{
                maxHeight: scaledHeight - shelfBottomHeight + 24 / scaleFactor,
                position: "absolute",
                left: scaledWidth - 48 / scaleFactor,
                top: 16 / scaleFactor,
                display: "block",
              }}
            />
          </div>
        </div>
      )}
      <div
        className={classes.flexCol}
        style={{
          width: scaledWidth,
          height: scaledHeight,
          marginLeft:
            id === 1
              ? 0
              : scaleFactor < 3
              ? shelfWrapperMargin - 8 * (id - 1)
              : shelfWrapperMargin - 1 * (id - 1),
          zIndex: 6,
          position: "absolute",
          top: 0,
        }}
      >
        {id === 1 && (
          <div
            className={classes.leftPanel}
            style={{
              display: "flex",
              position: "absolute",
              top: 0,
              height: scaledHeight - shelfBottomHeight,
            }}
          >
            <img
              src={`${baseUrl}/images/item/bert/warehouse/image/frame_height_${height}_${color}_front.svg`}
              className={classes.leftFrame1}
              style={{
                maxHeight: scaledHeight - shelfBottomHeight + 24 / scaleFactor,
                position: "absolute",
                left: 0,
                marginTop: shelfBottomHeight - 16 / scaleFactor,
                zIndex: 14,
                display: "block",
              }}
            />
            {!leftSidePanel ? (
              <img
                src={`${baseUrl}/images/item/bert/warehouse/image/side_singlepanels_height_${height}_depth_${depth}_${color}.svg`}
                className={classes.leftSidePanel}
                style={{
                  maxHeight: scaledHeight - shelfBottomHeight + 24 / scaleFactor,
                  position: "absolute",
                  left: -16 / scaleFactor,
                  marginTop: shelfBottomHeight,
                }}
              />
            ) : (
              <div
                className={classes.leftSidePanel}
                style={{
                  backgroundImage: `url(${baseUrl}/images/item/bert/warehouse/image/side_fullpanel_height_${height}_depth_${depth}.svg)`,
                  minHeight: depth === 800 ? scaledHeight + 24 / scaleFactor : scaledHeight,
                  minWidth: shelfBottomHeight,
                  marginTop: depth === 800 ? -24 / scaleFactor : 0,
                  backgroundSize: "cover",
                  position: "absolute",
                  left: -16 / scaleFactor,
                }}
              ></div>
            )}
            <img
              src={`${baseUrl}/images/item/bert/warehouse/image/frame_height_${height}_${color}_back.svg`}
              style={{
                maxHeight: scaledHeight - shelfBottomHeight + 24 / scaleFactor,
                position: "absolute",
                left: shelfBottomHeight - 32 / scaleFactor,
                top: 16 / scaleFactor,
                display: "block",
              }}
            />
          </div>
        )}
        {id !== 1 && id !== 2 && (
          <IconButton
            className={classes.deleteButton}
            onClick={removeCurrentField}
            style={{ left: `calc(50% - 21px - ${shelfBottomHeightDifference}px)` }}
          >
            <RemoveIcon style={{ color: "#262626" }} />
          </IconButton>
        )}
        <div
          className={classes.wrapper}
          style={{
            minHeight: scaledHeight - 48 / scaleFactor,
            maxHeight: scaledHeight,
            minWidth: scaledWidth,
            width: scaledWidth,
          }}
        >
          {rowElements}
        </div>
        {!twoSidedShelf && (
          <div
            className={classes.wrapper}
            style={{
              minHeight: scaledHeight - 48 / scaleFactor,
              maxHeight: scaledHeight,
              minWidth: scaledWidth,
              position: "absolute",
              left: 0,
              top: 0,
              width: scaledWidth,
            }}
          >
            {idIsOdd && (
              <img
                src={`${baseUrl}/images/item/bert/warehouse/image/diagonals_width_${width}.svg`}
                className={classes.leftFrame1}
                style={{
                  minHeight: scaledHeight + shelfBottomHeight,
                  maxWidth: scaledWidth - shelfBottomHeight,
                  position: "absolute",
                  left: shelfBottomHeight,
                  top: 0,
                }}
              />
            )}
          </div>
        )}
        <div
          className={classes.rightPanel}
          style={{
            display: "flex",
            position: "absolute",
            top: 0,
            height: scaledHeight - shelfBottomHeight,
          }}
        >
          <img
            src={`${baseUrl}/images/item/bert/warehouse/image/frame_height_${height}_${color}_front.svg`}
            style={{
              maxHeight: scaledHeight - shelfBottomHeight + 24 / scaleFactor,
              position: "absolute",
              right: -scaledWidth + shelfBottomHeight - 32 / scaleFactor,
              marginTop: shelfBottomHeight - 16 / scaleFactor,
              zIndex: 14,
              display: "block",
            }}
          />
          {!rightSidePanel ? (
            <img
              src={`${baseUrl}/images/item/bert/warehouse/image/side_singlepanels_height_${height}_depth_${depth}_${color}.svg`}
              className={classes.rightSidePanel}
              style={{
                maxHeight: scaledHeight - shelfBottomHeight + 24 / scaleFactor,
                position: "absolute",
                marginTop: shelfBottomHeight,
                right:
                  depth === 800 && height === 2500
                    ? -scaledWidth - 24 / scaleFactor
                    : depth === 800 && height === 3000
                    ? -scaledWidth - 32 / scaleFactor
                    : -scaledWidth - 16 / scaleFactor,
                zIndex: 13,
              }}
            />
          ) : (
            <div
              className={classes.rightSidePanel}
              style={{
                backgroundImage: `url(${baseUrl}/images/item/bert/warehouse/image/side_fullpanel_height_${height}_depth_${depth}.svg)`,
                minHeight: depth === 800 ? scaledHeight + 24 / scaleFactor : scaledHeight,
                minWidth: shelfBottomHeight,
                marginTop: depth === 800 ? -24 / scaleFactor : 0,
                backgroundSize: "cover",
                position: "absolute",
                right: -scaledWidth + 24 / scaleFactor,
                zIndex: 13,
              }}
            ></div>
          )}
          <img
            src={`${baseUrl}/images/item/bert/warehouse/image/frame_height_${height}_${color}_back.svg`}
            style={{
              maxHeight: scaledHeight - shelfBottomHeight + 24 / scaleFactor,
              position: "absolute",
              left: scaledWidth - 48 / scaleFactor,
              top: 16 / scaleFactor,
              display: "block",
            }}
          />
        </div>
        <div className={classes.shelfHeight}>
          <div>{t("common:shelfHeight")}</div>
          <div>{`${shelfHeight} mm`}</div>
        </div>
        <div className={classes.shelfWidth} style={{ width: scaledWidth - shelfWidthOffset }}>
          <div className={classes.arrow}>
            <img src={isSmallScreen ? arrowUpSmall : arrowUp} style={{ transform: "rotate(-90deg)" }} />
            <div
              style={{
                width: "100%",
                height: isSmallScreen ? 3.5 : 4.5,
                borderBottom: "1px solid #979797",
                marginLeft: -3,
              }}
            />
          </div>
          <div
            style={{
              marginLeft: isSmallScreen ? widthContainerMarginSmallScreen : widthContainerMargin,
              marginRight: isSmallScreen ? widthContainerMarginSmallScreen : widthContainerMargin,
              minWidth: isSmallScreen ? widthContainerWidthSmallScreen : widthContainerWidth,
              fontSize: isSmallScreen ? 10 : 14,
            }}
          >{`${width} mm`}</div>
          <div className={classes.arrow}>
            <div
              style={{
                width: "100%",
                height: isSmallScreen ? 3.5 : 4.5,
                borderBottom: "1px solid #979797",
                marginRight: -3,
              }}
            />
            <img src={isSmallScreen ? arrowDownSmall : arrowDown} style={{ transform: "rotate(-90deg)" }} />
          </div>
        </div>
        {optionsLoaded && (
          <IconButton
            className={classes.iconButton}
            style={{ left: `calc(50% - 21px - ${shelfBottomHeightDifference}px)` }}
            onClick={(e) => {
              setShowFieldOptions(true);
            }}
          >
            <EditIcon style={{ color: "#262626" }} />
          </IconButton>
        )}
        {showFieldOptions && (
          <FieldOptionsDialog
            show={true}
            onClose={() => setShowFieldOptions(false)}
            width={widthIdentifier}
            leftSidePanel={leftSidePanel}
            rightSidePanel={rightSidePanel}
            supportBars={supportbarIdentifier}
            updateSupportBarsAmount={(val) => updateSupportBarsAmount(val)}
            updateWidth={(val) => updateWidth(val)}
            updateLeftSidePanel={(val) => updateLeftSidePanel(val)}
            updateRightSidePanel={(val) => updateRightSidePanel(val)}
          />
        )}
      </div>
    </div>
  );
};

export default FrontViewIsoWarehouse;
